import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Content,
  Item,
  Chip,
  ChipContent,
  ChipName,
  ChipEmail,
  EmailSent,
  EmailSentLabel,
  DeleteItem,
  Recipients,
  FooterOption,
} from './ShareResultsModal.styles';
import SearchDropdown from '../../components/SearchDropdown/SearchDropdown';
import Modal from '../../HOC/Modal/Modal';
import { useShareSurvey } from '../../hooks';
import { IShareUser } from '../../services/surveys';
import Checkbox from '../Checkbox/Checkbox';
import { ISearchDropdownItem } from '../SearchDropdown/SearchDropdown';
import CloseMark from '@/assets/icons/closeMark.svg?react';
import SuccessNotificationIcon from '@/assets/icons/successNotificationIcon.svg?react';

interface IShareResultsModal {
  selectedReport?: string;
  title: string;
  closeModal: () => void;
  recipients: (IShareUser & { shared?: boolean })[];
  surveyId: string;
  departmentId: number;
  processReports: () => void;
  surveyType?: string;
}

const ShareResultsModal: FC<IShareResultsModal> = ({
  selectedReport,
  closeModal,
  title,
  recipients,
  surveyId,
  departmentId,
  processReports,
  surveyType,
}) => {
  const [selectedRecipients, setSelectedRecipients] = useState<(IShareUser & { shared?: boolean })[]>(
    recipients
  );
  const [submitDisabled, setSubmitDisabled] = useState<boolean>(false);
  const [applyForChild, setApplyForChild] = useState<boolean>(false);
  const [allItems, setAllItems] = useState<(IShareUser & { shared?: boolean })[]>([]);
  const [filteredItems, setFilteredItems] = useState<(IShareUser & { shared?: boolean })[]>([]);
  const { t } = useTranslation();

  const { getSurveyShareUsers, putSurveyShareUsers } = useShareSurvey(surveyId);

  const resetAndCloseModal = () => {
    setApplyForChild(false);
    closeModal();
  };

  const getUsers = async () => {
    const users = await getSurveyShareUsers();
    setAllItems(users);
  };

  useEffect(() => {
    (async () => await getUsers())();
  }, [surveyId]);

  useEffect(() => {
    setSelectedRecipients(recipients);
  }, [recipients]);

  const excludeSelectedRecipients = (rec: IShareUser) =>
    !selectedRecipients.map(rec => rec.id).includes(rec?.id);

  const items = (filteredItems?.filter(excludeSelectedRecipients) || []).map((rec: IShareUser) => ({
    id: String(rec.id),
    name: (
      <Item data-testid="dropElement">
        <div data-testid="dropElement">{rec.name || '-'}</div>
        <div data-testid="dropElement">{rec.email}</div>
      </Item>
    ),
  }));

  const doFilter = (phrase: string) => {
    setFilteredItems(
      allItems.filter(
        (data: IShareUser) =>
          data.name.toLowerCase().includes(phrase.toLowerCase()) ||
          data.email.toLowerCase().includes(phrase.toLowerCase())
      )
    );
  };

  const selectEl = (item: string | ISearchDropdownItem) => {
    if (typeof item === 'string') {
      return;
    }
    const selectedId = Number(item.id);
    if (!Number.isNaN(selectedId)) {
      const selectedItem = allItems.find(i => i.id === selectedId);
      if (selectedItem) {
        setSelectedRecipients([...selectedRecipients, selectedItem]);
      }
    }
  };

  const saveUsers = async () => {
    setSubmitDisabled(true);

    const data = await putSurveyShareUsers(
      departmentId,
      selectedRecipients.map(rec => rec.id),
      applyForChild
    );

    if (data) {
      setSubmitDisabled(false);
      (async () => await processReports())();
      resetAndCloseModal();
    }
  };

  return (
    <Modal
      active={!!selectedReport}
      callToAction={saveUsers}
      closeModal={resetAndCloseModal}
      loading={submitDisabled}
      footerStartContent={
        surveyType !== 'EFFECT' ? (
          <FooterOption>
            <Checkbox
              checked={applyForChild}
              onClick={() => setApplyForChild(prev => !prev)}
            />
            {t('shared.modal.applyForChild')}
          </FooterOption>
        ) : undefined
      }
      absolute
      top="15vh"
      right="200px"
      width="868px"
      buttonNames={{
        action: t('viewSurvey.sharing.modal.save'),
        cancel: t('viewSurvey.sharing.modal.cancel'),
      }}
      title={title}
    >
      <Content>
        <SearchDropdown
          width="100%"
          items={items}
          placeholder={t('viewSurvey.sharing.modal.placeholder')}
          noResultsMessage={t('viewSurvey.sharing.modal.noResults')}
          searchMode
          doFilter={doFilter}
          selectEl={selectEl}
          selectFullObject
          label={t('viewSurvey.sharing.modal.shareWith')}
        />
        <Recipients>
          {selectedRecipients.map(item => {
            return (
              <Chip key={item?.id}>
                <ChipContent>
                  <ChipName>{item?.name || '-'}</ChipName>
                  <ChipEmail>
                    {item?.email}
                    {item?.shared && (
                      <EmailSent>
                        <SuccessNotificationIcon />
                        <EmailSentLabel>{t('viewSurvey.sharing.modal.shared')}</EmailSentLabel>
                      </EmailSent>
                    )}
                  </ChipEmail>
                </ChipContent>
                <DeleteItem
                  onClick={() => {
                    setSelectedRecipients(selectedRecipients.filter(rec => rec.id !== item?.id));
                  }}
                >
                  <CloseMark />
                </DeleteItem>
              </Chip>
            );
          })}
        </Recipients>
      </Content>
    </Modal>
  );
};

export default ShareResultsModal;
